import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Modal, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, {
  Easing,
  FadeIn,
  ZoomIn,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

import { BragCard, type BragCardData } from '@/components/share/BragCard';
import { AppText } from '@/components/ui/AppText';
import { Button } from '@/components/ui/Button';
import { palette } from '@/config/theme';
import { useWallet } from '@/features/games/rewards';
import { useMyPredictionStats, useRecentWin } from '@/features/predictions/game';
import { useProfile } from '@/features/profile/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { RecentWin } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { localizeTeamName } from '@/lib/i18n/footballNames';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { captureAndShare } from '@/lib/share/captureAndShare';

const APP_LINK = 'https://play.google.com/store/apps/details?id=com.tashkeela.app';

/** AsyncStorage marker: ISO timestamp of the most-recent win already celebrated. */
const LAST_BRAG_SEEN_KEY = 'tashkeela.lastBragSeen';

/**
 * Auto brag-card celebration. Mounted ONCE near the app root (sibling of
 * AppUpdateGate). On launch it asks the data layer for the user's most-recent
 * SETTLED WINNING prediction across the three games; if that win is NEWER than
 * the timestamp we last celebrated (persisted in AsyncStorage), it pops a
 * premium modal exactly once. Dismissing OR sharing advances the marker, so the
 * same win never re-pops. Renders null whenever there's nothing new to
 * celebrate — so it is inert on the overwhelming majority of launches.
 *
 * The card shown + captured is the existing season BragCard (rank / points /
 * streak), framed by a win headline — reusing the profile's share contract
 * verbatim rather than inventing a second capturable surface.
 */
export function WinCelebrationGate() {
  const { t } = useTranslation();
  const { userId } = useAuth();
  const { locale, numeralOptions, isRTL } = useLocale();
  const n = useCallback((x: number) => formatNumber(x, numeralOptions), [numeralOptions]);

  const winQuery = useRecentWin();
  const statsQuery = useMyPredictionStats();
  const walletQuery = useWallet();
  const profileQuery = useProfile();

  // The marker is read once on mount. `null` = not yet loaded (never decide
  // before we know what was already seen); a string = the last celebrated ISO.
  const [marker, setMarker] = useState<string | null>(null);
  const [markerLoaded, setMarkerLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [sharing, setSharing] = useState(false);
  // Only celebrate while the app is genuinely foreground — never mid cold-start
  // deep-link / notification route (same race the root listeners guard against).
  const [foreground, setForeground] = useState(AppState.currentState === 'active');

  const bragRef = useRef<View>(null);

  useEffect(() => {
    let alive = true;
    AsyncStorage.getItem(LAST_BRAG_SEEN_KEY)
      .then((value) => {
        if (!alive) return;
        setMarker(value);
        setMarkerLoaded(true);
      })
      .catch(() => {
        // Storage unavailable — treat as "nothing celebrated yet" but still let
        // the gate run so a real win isn't silently swallowed.
        if (!alive) return;
        setMarkerLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      setForeground(state === 'active');
    });
    return () => sub.remove();
  }, []);

  const win = winQuery.data ?? null;

  // The single source of truth for "show it": a fresh win, the marker resolved,
  // signed in, foreground, not already dismissed this session.
  const isFresh =
    !!win &&
    markerLoaded &&
    (marker === null || win.settledAt.localeCompare(marker) > 0);

  const visible = Boolean(userId) && foreground && isFresh && !dismissed;

  // Season card data — mirrors the profile's manual brag exactly so the captured
  // image is the same signature card. Falls back gracefully when stats are still
  // loading (the card simply shows zeros until they arrive; the gate only opens
  // once a win exists, and stats almost always resolve first).
  const stats = statsQuery.data;
  const displayName = profileQuery.data?.display_name ?? t('brag.aPlayer');
  const bragData: BragCardData = useMemo(
    () => ({
      name: displayName || t('brag.aPlayer'),
      avatarUrl: profileQuery.data?.avatar_url,
      rank: stats?.rank ?? 0,
      points: stats?.total_points ?? 0,
      correct: stats?.correct ?? 0,
      streak: walletQuery.data?.streak ?? 0,
    }),
    [displayName, profileQuery.data?.avatar_url, stats?.rank, stats?.total_points, stats?.correct, walletQuery.data?.streak, t],
  );

  const matchLine = useMemo(() => buildMatchLine(win, locale, isRTL, n, t), [win, locale, isRTL, n, t]);
  const rewardLine = useMemo(() => {
    if (!win) return '';
    if (win.coins > 0) return t('winCelebration.wonCoins', { coins: n(win.coins) });
    if (win.points > 0) return t('winCelebration.wonPoints', { points: n(win.points) });
    return t('winCelebration.wonPlain');
  }, [win, n, t]);

  const advanceMarker = useCallback(() => {
    if (!win) return;
    setMarker(win.settledAt);
    AsyncStorage.setItem(LAST_BRAG_SEEN_KEY, win.settledAt).catch(() => {
      // Best-effort: if the write fails the worst case is one repeat pop.
    });
  }, [win]);

  const onDone = useCallback(() => {
    setDismissed(true);
    advanceMarker();
  }, [advanceMarker]);

  const onShare = useCallback(async () => {
    if (!win) return;
    setSharing(true);
    try {
      const reward = win.coins > 0 ? { coins: n(win.coins) } : { points: n(win.points) };
      const matchPlain = matchLine || undefined;
      const headline =
        win.coins > 0
          ? matchPlain
            ? t('winCelebration.shareLineCoins', { match: matchPlain, ...reward })
            : t('winCelebration.shareLinePlain')
          : win.points > 0 && matchPlain
            ? t('winCelebration.shareLinePoints', { match: matchPlain, ...reward })
            : t('winCelebration.shareLinePlain');
      const message = `${headline}\n${t('common.viaApp')} ${APP_LINK}`;
      await captureAndShare({ viewRef: bragRef, message, dialogTitle: t('winCelebration.title') });
    } finally {
      setSharing(false);
      // Sharing (success OR cancel) counts as "seen" — the user has engaged with
      // this win; we never re-nag for it.
      setDismissed(true);
      advanceMarker();
    }
  }, [win, n, matchLine, t, advanceMarker]);

  if (!visible) return null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onDone} statusBarTranslucent>
      <Animated.View
        entering={FadeIn.duration(180)}
        className="flex-1 items-center justify-center px-6"
        style={{ backgroundColor: 'rgba(0,0,0,0.74)' }}
      >
        <Pressable className="absolute inset-0" accessibilityRole="button" onPress={!sharing ? onDone : undefined} />

        <Animated.View
          entering={ZoomIn.springify().damping(16)}
          className="w-full items-center rounded-3xl border border-volt-line bg-surface-2 px-5 pb-5 pt-6"
          style={{ maxWidth: 360, overflow: 'hidden' }}
        >
          {/* Celebratory wash behind the header */}
          <LinearGradient
            colors={['rgba(52,211,153,0.22)', 'rgba(34,211,255,0.10)', 'rgba(255,255,255,0)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 180 }}
          />

          <SparkleBurst />

          <AppText variant="title" weight="bold" className="mt-3 text-center text-ink">
            {t('winCelebration.title')}
          </AppText>
          {matchLine ? (
            <AppText variant="body" weight="semibold" className="mt-1 text-center text-soft">
              {matchLine}
            </AppText>
          ) : null}
          <AppText variant="caption" className="mt-1 text-center text-muted">
            {rewardLine}
          </AppText>

          {/* The signature season card, shown AND captured from the same ref. */}
          <View
            className="mt-4 items-center"
            collapsable={false}
            style={{ transform: [{ scale: 0.92 }] }}
          >
            <BragCard ref={bragRef} data={bragData} />
          </View>

          <View className="mt-5 w-full">
            <Button
              label={t('winCelebration.share')}
              variant="primary"
              loading={sharing}
              onPress={onShare}
            />
          </View>
          <Pressable
            accessibilityRole="button"
            onPress={onDone}
            disabled={sharing}
            className="mt-2 items-center py-2"
          >
            <AppText variant="micro" className="text-muted">
              {t('winCelebration.done')}
            </AppText>
          </Pressable>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}

/** Localised "Home 2 - 1 Away" headline, or just the matchup if the score is
 *  not on the row yet. Returns '' when neither team is known. */
function buildMatchLine(
  win: RecentWin | null,
  locale: 'ar' | 'en',
  isRTL: boolean,
  n: (x: number) => string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!win) return '';
  const home = localizeTeamName(win.homeTeam, locale);
  const away = localizeTeamName(win.awayTeam, locale);
  if (home === '-' && away === '-') return '';
  const hasScore = win.homeScore != null && win.awayScore != null;
  // The app-wide score gotcha: RN renders the score Text LTR, so in Arabic the
  // digits must be REVERSED (away-home) AND bidi-isolated so each number sits
  // next to the right team. Mirrors AnimatedScore / ScoreShareCard / MatchH2H —
  // before this, Panama 0-2 England rendered as "بنما 2 - 0 إنجلترا" (swapped).
  const hs = hasScore ? n(win.homeScore as number) : '';
  const as = hasScore ? n(win.awayScore as number) : '';
  const score = hasScore ? `⁦${isRTL ? `${as} - ${hs}` : `${hs} - ${as}`}⁩` : '';
  return hasScore
    ? t('winCelebration.matchLine', { home, away, score })
    : `${home} - ${away}`;
}

/**
 * A compact ring of sparkles that twinkle once on entrance — the celebratory
 * header. Pure reanimated (no new dependency, no native confetti module). Fully
 * static under reduce-motion: the sparkles render in place without animating.
 */
function SparkleBurst() {
  const reduce = useReducedMotion();
  return (
    <View className="h-16 w-16 items-center justify-center rounded-full bg-volt-glass-strong">
      <Ionicons name="checkmark-circle" size={32} color={palette.win} />
      <Sparkle reduce={reduce} delay={0} x={-30} y={-18} size={14} />
      <Sparkle reduce={reduce} delay={120} x={28} y={-22} size={11} />
      <Sparkle reduce={reduce} delay={220} x={32} y={16} size={13} />
      <Sparkle reduce={reduce} delay={320} x={-28} y={18} size={10} />
    </View>
  );
}

function Sparkle({
  reduce,
  delay,
  x,
  y,
  size,
}: {
  reduce: boolean;
  delay: number;
  x: number;
  y: number;
  size: number;
}) {
  const progress = useSharedValue(reduce ? 1 : 0);

  useEffect(() => {
    if (reduce) {
      progress.value = 1;
      return;
    }
    progress.value = withDelay(
      delay,
      withRepeat(
        withSequence(
          withTiming(1, { duration: 420, easing: Easing.out(Easing.quad) }),
          withTiming(0.55, { duration: 520, easing: Easing.in(Easing.quad) }),
        ),
        -1,
        true,
      ),
    );
  }, [reduce, delay, progress]);

  const style = useAnimatedStyle(() => ({
    opacity: progress.value,
    transform: [{ scale: 0.6 + progress.value * 0.6 }],
  }));

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        {
          position: 'absolute',
          left: 32 + x - size / 2,
          top: 32 + y - size / 2,
        },
        style,
      ]}
    >
      <Ionicons name="sparkles" size={size} color={palette.captain} />
    </Animated.View>
  );
}
